import "../step.js";
import "../callout.js";
import "../mixins/appointment_slot_picker.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Views ??= {};

AppointmentBooking.Wizard.Views.WelcomeStep = class WelcomeStep extends (
  AppointmentBooking.Wizard.Views.Mixins.AppointmentSlotPicker(AppointmentBooking.Wizard.Views.Step)
) {
  constructor() {
    super(...arguments);
    this.isDisabled = this.isDisabled.bind(this);
    this.beforeShow = this.beforeShow.bind(this);
    this.initialize = this.initialize.bind(this);
    this.areRequirementsValid = this.areRequirementsValid.bind(this);
    this.afterShow = this.afterShow.bind(this);
    this.render = this.render.bind(this);
    this.handleInitialSlotLoad = this.handleInitialSlotLoad.bind(this);
    this.completeOrShowSlotSuggestor = this.completeOrShowSlotSuggestor.bind(this);
    this.whenSlotSuggesterLoaded = this.whenSlotSuggesterLoaded.bind(this);
    this.initializeSlotSuggester = this.initializeSlotSuggester.bind(this);
    this.handleSendToPortal = this.handleSendToPortal.bind(this);
    this.decorateField = this.decorateField.bind(this);
    this.welcomeComplete = this.welcomeComplete.bind(this);
  }

  autoContinue() {
    return true;
  }

  isDisabled() {
    return this.model.get("visit_type_id") && !this.decisionTreeId;
  }

  beforeShow() {
    this.model.set({ welcome_complete: false });

    return this.whenSlotSuggesterLoaded((suggester) => suggester.setSelectedSlotId(null));
  }

  initialize() {
    super.initialize(...arguments);

    this.listenToOnce(this.model, "change:visit_type_id", this.handleInitialSlotLoad);

    return this.whenSlotSuggesterLoaded((suggester) =>
      this.listenToIfEnabled(suggester.model, "change:selectedSlotId", (_, value) => {
        if (!value) {
          return;
        }

        const sameProvider =
          suggester.model.get("suggestedSlots").get(value).get("provider").get("id") === this.model.get("provider").id;

        return this.model.set({
          ehr_appointment_slot_id: value,
          welcome_complete: true,

          slot_suggester_success: true,
          reselection_result: "success",
          reselection_type: sameProvider ? "new_time" : "new_provider",
        });
      }),
    );
  }

  areRequirementsValid() {
    return this.welcomeComplete();
  }

  afterShow() {
    if (!this.showSlotSuggestor) {
      return this.decorateField();
    }
  }

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);

    this.whenSlotSuggesterLoaded((suggester) => {
      if (this.showSlotSuggestor) {
        return this.replaceContents(".slot-reselector-container", suggester.el);
      }
    });

    return this;
  }

  // EVENT HANDLERS

  handleInitialSlotLoad() {
    // We need to retain the original slot and decision tree ID so we can render the original decision tree
    // if the user manually navigates back to the welcome step
    this.defaultPatientType = this.model.get("returning_patient") ? "returning" : "new";
    this.appointmentSlotId = this.model.get("ehr_appointment_slot_id");
    this.decisionTreeId = this.model.get("decision_tree_id");

    this.model.set({ decision_tree_enabled: !!this.decisionTreeId });

    return this.decorateField();
  }

  handleProgress(event) {
    event = event.originalEvent;
    this.currentState = event.detail.currentState;

    this.model.set({ decision_tree_current_node_id: this.currentState.currentNodeId });

    this.triggerEvent(AppointmentBooking.Wizard.Events.Progress.setCurrentStepProgress, event.detail.value);

    if (event.detail.nodeId) {
      const viewedDetail = {
        decision_tree_step_node_type: event.detail.nodeType,
        decision_tree_step_node_id: event.detail.nodeId,
        decision_tree_step_question: event.detail.questionText,
        decision_tree_step_action: "viewed",
      };
      const viewedEvent = jQuery.Event("appointment-booking-wizard-analytics", { detail: viewedDetail });
      this.$el.trigger(viewedEvent, "decision-tree-step");
    }

    if (event.detail.completedNodeAnalytics) {
      const analytics = event.detail.completedNodeAnalytics;
      const stepDetail = {
        decision_tree_step_node_type: analytics.nodeType,
        decision_tree_step_node_id: analytics.nodeId,
        decision_tree_step_question: analytics.questionText,
        decision_tree_step_answer: analytics.selectedAnswer,
        decision_tree_step_answer_id: analytics.selectedAnswerId,
        decision_tree_step_sequence: event.detail.stepSequence,
        decision_tree_step_action: "completed",
      };
      const analyticsEvent = jQuery.Event("appointment-booking-wizard-analytics", { detail: stepDetail });
      this.$el.trigger(analyticsEvent, "decision-tree-step");
    }
  }

  handleAllowBooking(event) {
    event = event.originalEvent;
    this.currentState = event.detail.currentState;

    const returning_patient = event.detail.patientType === "returning";
    const visit_type_id = event.detail.visitTypeId;
    const age_range = event.detail.ageRange;
    const switch_provider_group_ids = event.detail.switchProviderGroupIds;
    const review_step_broadcasts = event.detail.reviewStepBroadcasts;
    const decision_tree_booking_metadata = event.detail.bookingMetadata;

    const provider = this.model.get("provider");
    const address = this.model.get("address");
    const slot = this.model.get("appointment_slot");

    this.whenSlotSuggesterLoaded((suggester) => {
      this.triggerEvent("loading.sparkle"); // Manually trigger loading state since the slot suggester is not in the DOM
      return suggester
        .setDesiredSlotConditions({
          id: slot.id,
          startTime: slot.start_time,
          departmentId: address.department_id,
          visitTypeId: visit_type_id,
          providerGroupIds: switch_provider_group_ids,
          patientType: event.detail.patientType,
          resourceId: provider.id,
          resourceType: provider.type,
        })
        .then(this.completeOrShowSlotSuggestor);
    });

    this.model.set({
      decision_tree_result: "allow_booking",
      returning_patient,
      decision_tree_age_range: age_range,
      switch_provider_group_ids,
      review_step_broadcasts,
      decision_tree_booking_metadata,
    });

    const exitDetail = {
      decision_tree_step_node_type: "AllowBookingNode",
      decision_tree_step_node_id: this.currentState.currentNodeId,
      decision_tree_step_is_exit: true,
      decision_tree_step_result: "allow_booking",
    };
    const exitEvent = jQuery.Event("appointment-booking-wizard-analytics", { detail: exitDetail });
    this.$el.trigger(exitEvent, "decision-tree-step");
  }

  // HELPERS

  completeOrShowSlotSuggestor(suggester) {
    this.triggerEvent("loaded.sparkle");
    const matchingSlot = suggester.model.get("matchingSlots").first();

    if (matchingSlot) {
      return this.model.set({ ehr_appointment_slot_id: matchingSlot.id, welcome_complete: true });
    } else {
      let reselectionResult;
      this.showSlotSuggestor = true;
      this.render();

      const reselectionReason = suggester.model.get("reselectionReason");
      if (suggester.model.get("suggestedSlots").length === 0) {
        reselectionResult = "no_slots";
      }

      this.model.set({
        slot_suggester_shown: true,
        reselection_reason: reselectionReason,
        reselection_result: reselectionResult,
      });

      return this.triggerEvent("appointment-booking-wizard-analytics", "pageview");
    }
  }

  whenSlotSuggesterLoaded(fn) {
    return this.initializeSlotSuggester().then(fn);
  }

  initializeSlotSuggester() {
    return (
      this.slotSuggesterPromise ||
      (this.slotSuggesterPromise = AppointmentBooking.initializeSlotSuggester({
        variant: "flat",
      }))
    );
  }

  handleDeny(event) {
    event = event.originalEvent;
    this.model.set({ decision_tree_result: "deny" });
    this.triggerEvent(AppointmentBooking.Wizard.Events.Progress.setCurrentStepProgress, 1);

    const exitDetail = {
      decision_tree_step_node_type: "DenyNode",
      decision_tree_step_node_id: event.detail.currentNodeId,
      decision_tree_step_is_exit: true,
      decision_tree_step_result: "deny",
    };
    const exitEvent = jQuery.Event("appointment-booking-wizard-analytics", { detail: exitDetail });
    this.$el.trigger(exitEvent, "decision-tree-step");
  }

  handleSendToPortal(event) {
    event = event.originalEvent;
    this.model.set({ decision_tree_result: "send_to_portal" });
    this.triggerEvent(AppointmentBooking.Wizard.Events.Progress.setCurrentStepProgress, 1);

    const exitDetail = {
      decision_tree_step_node_type: "SendToPortalNode",
      decision_tree_step_node_id: event.detail.currentNodeId,
      decision_tree_step_is_exit: true,
      decision_tree_step_result: "send_to_portal",
    };
    const exitEvent = jQuery.Event("appointment-booking-wizard-analytics", { detail: exitDetail });
    this.$el.trigger(exitEvent, "decision-tree-step");
  }

  // PRIVATE

  decorateField() {
    if (!this.decisionTreeId) {
      return;
    }

    const event = new CustomEvent("decision-tree-viewer:mount", {
      detail: {
        selector: "#decision-tree-viewer",
        props: {
          defaultPatientType: this.defaultPatientType,
          appointmentSlotId: this.appointmentSlotId,
          decisionTreeId: this.decisionTreeId,
          currentState: this.currentState,
        },
      },
    });

    return document.dispatchEvent(event);
  }

  welcomeComplete() {
    return this.model.get("welcome_complete");
  }
};
AppointmentBooking.Wizard.Views.WelcomeStep.prototype.template = "step/welcome";
AppointmentBooking.Wizard.Views.WelcomeStep.prototype.name = "Welcome";
AppointmentBooking.Wizard.Views.WelcomeStep.prototype.className = "appointment-booking-wizard-welcome-step";

AppointmentBooking.Wizard.Views.WelcomeStep.prototype.events = {
  "decision-tree:progress #decision-tree-viewer": "handleProgress",
  "decision-tree:allow-booking #decision-tree-viewer": "handleAllowBooking",
  "decision-tree:deny #decision-tree-viewer": "handleDeny",
  "decision-tree:send-to-portal #decision-tree-viewer": "handleSendToPortal",
};
